import React, { useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import Swal from "sweetalert2";
import { API_BASE } from "../utils/apiBase";

const SHORTCODE_SAMPLES = [
  "{{name}}",
  "{{application_no}}",
  "{{registration_no}}",
  "{{user_id}}",
  "{{password}}",
  "{{message}}",
];

function NotificationSettingsPage() {
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch (error) {
      return {};
    }
  })();
  const resolvedActorId = Number(currentUser?.id || 0) > 0 ? Number(currentUser.id) : 1;
  const adminAuthParams = {
    actor_type: "admin",
    actor_id: resolvedActorId,
  };
  const [settings, setSettings] = useState({
    smtp_host: "",
    smtp_port: "587",
    smtp_username: "",
    smtp_password: "",
    smtp_encryption: "tls",
    from_name: "",
    from_address: "",
    sender_image_url: "",
    mail_signature_html: "<p>Thanks & Regards,<br><b>NIDCS Team</b></p>",
  });
  const [templates, setTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState({ key: "", subject: "", body: "" });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [perPage, setPerPage] = useState("10");
  const [currentPage, setCurrentPage] = useState(1);

  const load = async () => {
    try {
      const res = await axios.get(`${API_BASE}/notification-settings`, { params: adminAuthParams });
      setSettings((prev) => ({ ...prev, ...(res.data?.settings || {}) }));
      setTemplates(Array.isArray(res.data?.templates) ? res.data.templates : []);
    } catch (error) {
      try {
        const res = await axios.get(`${API_BASE}/notification-settings`, {
          params: { actor_type: "admin", actor_id: 1 },
        });
        setSettings((prev) => ({ ...prev, ...(res.data?.settings || {}) }));
        setTemplates(Array.isArray(res.data?.templates) ? res.data.templates : []);
      } catch {
        Swal.fire("Load failed", "Notification settings load korte problem hocche.", "error");
      }
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveSettings = async () => {
    try {
      await axios.post(`${API_BASE}/notification-settings`, { ...settings, ...adminAuthParams });
      Swal.fire("Saved", "Notification settings successfully saved.", "success");
    } catch (error) {
      Swal.fire("Save failed", error?.response?.data?.message || "Settings save korte problem hocche.", "error");
    }
  };

  const applyMailgunPreset = () => {
    setSettings((prev) => ({
      ...prev,
      smtp_host: "smtp.mailgun.org",
      smtp_port: "587",
      smtp_encryption: "tls",
    }));
    Swal.fire("Preset Applied", "Mailgun SMTP preset apply hoye geche. Username/password boshiye save korun.", "success");
  };

  const sendTestEmail = async () => {
    if (!testEmail.trim()) {
      Swal.fire("Missing email", "Test recipient email din.", "warning");
      return;
    }
    try {
      await axios.post(`${API_BASE}/admin/communication/send-email`, {
        to: testEmail.trim(),
        subject: "NIDCS Notification System Test",
        body: "<p>Notification settings test successful.</p>",
      });
      Swal.fire("Sent", "Test email sent successfully.", "success");
    } catch (error) {
      Swal.fire("Send failed", error?.response?.data?.message || "Test email send korte problem hocche.", "error");
    }
  };

  const saveTemplate = async (item, closeModal = false) => {
    try {
      await axios.post(`${API_BASE}/notification-templates/${item.key}`, {
        subject: item.subject || "",
        body: item.body || "",
        ...adminAuthParams,
      });
      Swal.fire("Saved", "Template successfully saved.", "success");
      if (closeModal) {
        setEditingTemplate(null);
      }
    } catch (error) {
      Swal.fire("Save failed", error?.response?.data?.message || "Template save korte problem hocche.", "error");
    }
  };

  const addTemplate = async () => {
    const templateKey = (newTemplate.key || "").trim().toLowerCase().replace(/\s+/g, "_");
    if (!templateKey) {
      Swal.fire("Missing key", "Template key din (example: welcome_notice).", "warning");
      return;
    }
    if (templates.some((t) => t.key === templateKey)) {
      Swal.fire("Duplicate key", "Ei key already ache.", "warning");
      return;
    }

    try {
      await axios.post(`${API_BASE}/notification-templates/${templateKey}`, {
        subject: newTemplate.subject || "",
        body: newTemplate.body || "",
        ...adminAuthParams,
      });
      setTemplates((prev) => [...prev, { key: templateKey, subject: newTemplate.subject || "", body: newTemplate.body || "" }]);
      setCurrentPage(1);
      setNewTemplate({ key: "", subject: "", body: "" });
      Swal.fire("Added", "New template added successfully.", "success");
    } catch (error) {
      Swal.fire("Add failed", error?.response?.data?.message || "Template add korte problem hocche.", "error");
    }
  };

  const deleteTemplate = async (templateKey) => {
    const ok = await Swal.fire({
      title: "Delete template?",
      text: `${templateKey} delete korte chan?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    });
    if (!ok.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE}/notification-templates/${templateKey}`, { data: adminAuthParams });
      setTemplates((prev) => {
        const next = prev.filter((t) => t.key !== templateKey);
        const size = perPage === "all" ? next.length || 1 : Number(perPage);
        const maxPage = Math.max(1, Math.ceil(next.length / size));
        if (currentPage > maxPage) setCurrentPage(maxPage);
        return next;
      });
      Swal.fire("Deleted", "Template deleted successfully.", "success");
    } catch (error) {
      Swal.fire("Delete failed", error?.response?.data?.message || "Template delete korte problem hocche.", "error");
    }
  };

  const totalRows = templates.length;
  const pageSize = perPage === "all" ? Math.max(1, totalRows) : Number(perPage);
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const pagedTemplates = perPage === "all" ? templates : templates.slice(startIndex, startIndex + pageSize);

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <h3 className="mb-3">Notification Settings</h3>
        <div className="d-flex gap-2 flex-wrap mb-3">
          <button className="btn btn-outline-primary btn-sm" onClick={applyMailgunPreset}>Apply Mailgun Preset</button>
        </div>
        <div className="row g-3">
          {Object.keys(settings).map((key) => (
            <div className="col-md-4" key={key}>
              <label className="form-label text-capitalize">{key.replaceAll("_", " ")}</label>
              {key === "mail_signature_html" ? (
                <textarea
                  className="form-control"
                  rows={4}
                  value={settings[key] || ""}
                  onChange={(e) => setSettings((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              ) : (
                <input
                  className="form-control"
                  type={key.includes("password") ? "password" : "text"}
                  value={settings[key] || ""}
                  onChange={(e) => setSettings((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              )}
            </div>
          ))}
        </div>
        <div className="d-flex gap-2 flex-wrap mt-3">
          <button className="btn btn-primary" onClick={saveSettings}>Save SMTP Settings</button>
          <input
            className="form-control"
            style={{ maxWidth: "280px" }}
            placeholder="Test recipient email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
          />
          <button className="btn btn-outline-success" onClick={sendTestEmail}>Send Test Email</button>
        </div>

        <hr className="my-4" />
        <h5>Add New Template</h5>
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Template Key</label>
                <input
                  className="form-control"
                  placeholder="example: welcome_notice"
                  value={newTemplate.key}
                  onChange={(e) => setNewTemplate((prev) => ({ ...prev, key: e.target.value }))}
                />
              </div>
              <div className="col-md-8">
                <label className="form-label">Subject</label>
                <input
                  className="form-control"
                  value={newTemplate.subject}
                  onChange={(e) => setNewTemplate((prev) => ({ ...prev, subject: e.target.value }))}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Body</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={newTemplate.body}
                  onChange={(e) => setNewTemplate((prev) => ({ ...prev, body: e.target.value }))}
                />
              </div>
            </div>
            <button className="btn btn-primary mt-3" onClick={addTemplate}>Add Template</button>
          </div>
        </div>

        <h6 className="mb-2">Shortcode Samples</h6>
        <div className="d-flex flex-wrap gap-2 mb-4">
          {SHORTCODE_SAMPLES.map((code) => (
            <span key={code} className="badge bg-light text-dark border" style={{ fontSize: "12px" }}>{code}</span>
          ))}
        </div>

        <h5>Mail Templates</h5>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2 mb-2">
          <div className="small text-muted">Total: {totalRows}</div>
          <div className="d-flex align-items-center gap-2">
            <label className="small text-muted mb-0">Show</label>
            <select
              className="form-select form-select-sm"
              style={{ width: "120px" }}
              value={perPage}
              onChange={(e) => {
                setPerPage(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>
        <div className="table-responsive mt-2">
          <table className="table table-bordered align-middle" style={{ fontSize: "13px", lineHeight: 1.45 }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "22%" }}>Template Key</th>
                <th style={{ width: "24%" }}>Subject</th>
                <th>Body</th>
                <th style={{ width: "180px" }} className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedTemplates.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center text-muted py-4">No template found.</td>
                </tr>
              ) : pagedTemplates.map((t, idx) => (
                <React.Fragment key={t.key || idx}>
                  <tr>
                    <td className="fw-semibold">{t.key}</td>
                    <td>{t.subject || "-"}</td>
                    <td style={{ maxWidth: "520px", whiteSpace: "normal", wordBreak: "break-word" }}>
                      {t.body || "-"}
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setEditingTemplate({ ...t })}
                        >
                          Edit
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => deleteTemplate(t.key)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {perPage !== "all" && totalPages > 1 ? (
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="small text-muted">
              Showing {startIndex + 1} - {Math.min(startIndex + pageSize, totalRows)} of {totalRows}
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={safeCurrentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span className="small align-self-center">Page {safeCurrentPage} / {totalPages}</span>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={safeCurrentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {editingTemplate ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div className="card" style={{ width: "min(900px, 100%)", maxHeight: "90vh", overflow: "auto" }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Edit Template: {editingTemplate.key}</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingTemplate(null)}>Close</button>
              </div>
              <div className="mb-2">
                <label className="form-label">Subject</label>
                <input
                  className="form-control"
                  value={editingTemplate.subject || ""}
                  onChange={(e) => setEditingTemplate((prev) => ({ ...prev, subject: e.target.value }))}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Body</label>
                <textarea
                  className="form-control"
                  rows={8}
                  value={editingTemplate.body || ""}
                  onChange={(e) => setEditingTemplate((prev) => ({ ...prev, body: e.target.value }))}
                />
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-success"
                  onClick={() => {
                    const copy = [...templates];
                    const i = copy.findIndex((x) => x.key === editingTemplate.key);
                    if (i >= 0) {
                      copy[i] = { ...editingTemplate };
                      setTemplates(copy);
                    }
                    saveTemplate(editingTemplate, true);
                  }}
                >
                  Save Changes
                </button>
                <button className="btn btn-outline-secondary" onClick={() => setEditingTemplate(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}

export default NotificationSettingsPage;



